/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */


import waitUntil, {TimeoutError} from 'async-wait-until';

/**
 * A value that tells the `forElement` function how to find a requested element.
 *
 * It can be one of the following:
 *  - A function that returns an element, if it is already present
 *    (or `null`/`undefined` if the requested element is not present yet).
 *
 *  - A CSS selector string. In this case this function will use the `document.querySelectorAll`
 *    function to find elements by the provided selector string, and will use the first of them
 *    whose `offsetParent` property has a non-null value. This additional `offsetParent` checking
 *    is used to ensure that the element is already displayed (doesn't have `display: none` in
 *    the parent chain).
 */
type ElementLocator = { (): (Element | undefined | null) } | string;

/**
 * Tries to find an element by element locator as described in the `ElementLocator` documentation.
 */
function tryToFindElement(
    elementLocator: ElementLocator
): Element | undefined | null {
  if (typeof elementLocator === "function") {
    return elementLocator();
  }
  const elements = document.querySelectorAll(elementLocator);
  if (elements.length === 0) {
    return null;
  }
  for (const el of elements) {
    if (!el) {
      continue;
    }
    if (!(el instanceof HTMLElement) || el.offsetParent) {
      return el;
    }
  }
  return null;
}

/**
 * An async function that awaits for the availability of the element obtained with the provided
 * `getElement` function.
 *
 * @param elementLocator  can either be a function that returns the respective element, or a CSS
 *                        selector string, see the description of `ElementLocator` type for more
 *                        information).
 *
 *  @param params  can be used to customize the interval between element searching attempts in
 *                 milliseconds (defaults to 20 ms), and a maximum duration that such attempts
 *                 to find an element should be performed for, in milliseconds
 *                 (defaults to 5000 ms).
 */
export async function forElement(
    elementLocator: {(): Element | undefined | null} | string,
    params?: {
      intervalBetweenAttempts: number,
      timeout: number
    }): Promise<Element> {
  const adaptedParams = Object.assign({}, {
    intervalBetweenAttempts: 20,
    timeout: 5000
  }, params);

  const el = await waitUntil(() => {
    const el = tryToFindElement(elementLocator);
    return el ? [el] : false;
  }, adaptedParams);
  if (!el|| !el[0]) {
    throw new TimeoutError(adaptedParams.timeout);
  }
  return el[0];
}
